import React, { useState, useEffect } from 'react';
import {
  Bell, Send, Users, Search, RefreshCw,
  BarChart3, Check, AlertCircle, Smartphone, Zap
} from 'lucide-react';
import './PushNotifications.css';

const TIER_OPTIONS = [
  { value: 'FREE', label: 'Free Only' },
  { value: 'PREMIUM', label: 'Premium Only' },
  { value: 'PRO', label: 'Pro Only' },
  { value: 'PRO PLUS', label: 'Pro Plus Only' },
];

const getTierClass = (tier) => `tier-${String(tier || 'FREE').toLowerCase().replace(/\s+/g, '-')}`;

const PushNotifications = () => {
  const [activeTab, setActiveTab] = useState('broadcast');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState({
    totalMerchants: 0,
    merchantsWithPushEnabled: 0,
    merchantsWithPushDisabled: 0,
    reachPercentage: 0,
    byTier: []
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Broadcast form state
  const [broadcastForm, setBroadcastForm] = useState({
    title: '',
    body: '',
    type: 'announcement',
    targetTier: 'all',
    targetLive: 'all'
  });

  // Single merchant form
  const [singleForm, setSingleForm] = useState({
    merchantId: '',
    merchantEmail: '',
    title: '',
    body: '',
    type: 'admin_message'
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/notifications/broadcast-stats', {
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setStats(data.stats);
      } else {
        setError(data.error || 'Failed to load push notification stats');
      }
    } catch (err) {
      console.error('Fetch stats error:', err);
      setError('Failed to load push notification stats');
    } finally {
      setLoading(false);
    }
  };

  const searchMerchants = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await fetch(`/api/admin/notifications/merchants?search=${encodeURIComponent(query)}&limit=10`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok && data.merchants) {
        setSearchResults(data.merchants);
      } else {
        setSearchResults([]);
        setError(data.error || 'Failed to search merchants');
      }
    } catch (err) {
      console.error('Search merchants error:', err);
      setSearchResults([]);
      setError('Failed to search merchants');
    }
  };

  const handleBroadcast = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!broadcastForm.title.trim() || !broadcastForm.body.trim()) {
      setError('Title and message are required');
      return;
    }

    try {
      setSending(true);
      const filters = {};
      if (broadcastForm.targetTier !== 'all') {
        filters.tier = broadcastForm.targetTier;
      }
      if (broadcastForm.targetLive !== 'all') {
        filters.isLive = broadcastForm.targetLive === 'live';
      }

      const response = await fetch('/api/admin/notifications/broadcast', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: broadcastForm.title,
          body: broadcastForm.body,
          type: broadcastForm.type,
          filters
        })
      });

      const data = await response.json();
      if (data.success) {
        const disabled = data.stats?.disabledByPreferences || 0;
        setSuccess(`Broadcast sent successfully. Delivered to ${data.stats?.successful || 0} merchants${disabled ? `; ${disabled} skipped by merchant preferences` : ''}.`);
        setBroadcastForm({
          title: '',
          body: '',
          type: 'announcement',
          targetTier: 'all',
          targetLive: 'all'
        });
        fetchStats();
      } else {
        setError(data.error || 'Failed to send broadcast');
      }
    } catch (err) {
      console.error('Broadcast error:', err);
      setError('Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  const handleSendToMerchant = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!singleForm.merchantId || !singleForm.title.trim() || !singleForm.body.trim()) {
      setError('Please select a merchant and fill in title and message');
      return;
    }

    try {
      setSending(true);
      const response = await fetch('/api/admin/notifications/send-to-merchant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          merchantId: singleForm.merchantId,
          title: singleForm.title,
          body: singleForm.body,
          type: singleForm.type
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Notification sent successfully!');
        setSingleForm({
          merchantId: '',
          merchantEmail: '',
          title: '',
          body: '',
          type: 'admin_message'
        });
        setSearchQuery('');
        setSearchResults([]);
      } else {
        const reasonCopy = {
          no_tokens: 'This merchant has no active mobile push token.',
          disabled_by_preferences: 'This merchant has disabled push notifications.',
          no_valid_tokens: 'This merchant has no valid push token.',
        };
        setError(data.error || reasonCopy[data.reason] || data.reason || 'Failed to send notification');
      }
    } catch (err) {
      console.error('Send notification error:', err);
      setError('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const selectMerchant = (merchant) => {
    setSingleForm({
      ...singleForm,
      merchantId: merchant.id,
      merchantEmail: merchant.email
    });
    setSearchQuery(merchant.email);
    setSearchResults([]);
  };

  return (
    <div className="push-notifications-container">
      <div className="push-notifications-header">
        <div className="push-header-left">
          <Bell size={28} />
          <div>
            <h1>Push Notifications</h1>
            <p>Send push notifications to merchant mobile apps</p>
          </div>
        </div>
        <button
          className="push-refresh-btn"
          onClick={fetchStats}
          disabled={loading}
        >
          <RefreshCw size={18} className={loading ? 'spinning' : ''} />
          Refresh Stats
        </button>
      </div>

      {/* Stats Cards */}
      <div className="push-stats-grid">
        <div className="push-stat-card">
          <div className="push-stat-icon" style={{ background: '#dbeafe' }}>
            <Users size={20} color="#1e40af" />
          </div>
          <div className="push-stat-value">{stats.totalMerchants}</div>
          <div className="push-stat-label">Total Merchants</div>
        </div>
        <div className="push-stat-card">
          <div className="push-stat-icon" style={{ background: '#d1fae5' }}>
            <Smartphone size={20} color="#065f46" />
          </div>
          <div className="push-stat-value">{stats.merchantsWithPushEnabled}</div>
          <div className="push-stat-label">Push Enabled</div>
        </div>
        <div className="push-stat-card">
          <div className="push-stat-icon" style={{ background: '#fef3c7' }}>
            <Zap size={20} color="#92400e" />
          </div>
          <div className="push-stat-value">{stats.reachPercentage}%</div>
          <div className="push-stat-label">Reach Rate</div>
        </div>
        <div className="push-stat-card">
          <div className="push-stat-icon" style={{ background: '#ede9fe' }}>
            <BarChart3 size={20} color="#5b21b6" />
          </div>
          <div className="push-stat-value">{stats.byTier?.length || 0}</div>
          <div className="push-stat-label">Tiers Active</div>
        </div>
      </div>

      {/* Tier Breakdown */}
      {stats.byTier && stats.byTier.length > 0 && (
        <div className="push-tier-breakdown">
          <h3>Merchants with Push by Tier</h3>
          <div className="push-tier-chips">
            {stats.byTier.map((t, i) => (
              <div key={i} className={`push-tier-chip ${getTierClass(t.tier)}`}>
                <span className="tier-name">{t.tier || 'FREE'}</span>
                <span className="tier-count">{t.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      {success && (
        <div className="push-message push-success">
          <Check size={18} />
          {success}
        </div>
      )}
      {error && (
        <div className="push-message push-error">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="push-tabs">
        <button
          className={`push-tab ${activeTab === 'broadcast' ? 'active' : ''}`}
          onClick={() => setActiveTab('broadcast')}
        >
          <Users size={18} />
          Broadcast to All
        </button>
        <button
          className={`push-tab ${activeTab === 'single' ? 'active' : ''}`}
          onClick={() => setActiveTab('single')}
        >
          <Send size={18} />
          Send to Merchant
        </button>
      </div>

      {/* Content */}
      <div className="push-content">
        {activeTab === 'broadcast' && (
          <form onSubmit={handleBroadcast} className="push-form">
            <div className="push-form-section">
              <h3>Notification Content</h3>

              <div className="push-form-group">
                <label>Notification Type</label>
                <select
                  value={broadcastForm.type}
                  onChange={(e) => setBroadcastForm({ ...broadcastForm, type: e.target.value })}
                >
                  <option value="announcement">Announcement</option>
                  <option value="promotion">Promotion</option>
                  <option value="update">Platform Update</option>
                  <option value="alert">Important Alert</option>
                </select>
              </div>

              <div className="push-form-group">
                <label>Title *</label>
                <input
                  type="text"
                  placeholder="e.g., New Feature Available!"
                  value={broadcastForm.title}
                  onChange={(e) => setBroadcastForm({ ...broadcastForm, title: e.target.value })}
                  maxLength={50}
                />
                <span className="char-count">{broadcastForm.title.length}/50</span>
              </div>

              <div className="push-form-group">
                <label>Message *</label>
                <textarea
                  placeholder="Enter your notification message..."
                  value={broadcastForm.body}
                  onChange={(e) => setBroadcastForm({ ...broadcastForm, body: e.target.value })}
                  rows={4}
                  maxLength={200}
                />
                <span className="char-count">{broadcastForm.body.length}/200</span>
              </div>
            </div>

            <div className="push-form-section">
              <h3>Target Audience</h3>

              <div className="push-form-row">
                <div className="push-form-group">
                  <label>Subscription Tier</label>
                  <select
                    value={broadcastForm.targetTier}
                    onChange={(e) => setBroadcastForm({ ...broadcastForm, targetTier: e.target.value })}
                  >
                    <option value="all">All Tiers</option>
                    {TIER_OPTIONS.map((tier) => (
                      <option key={tier.value} value={tier.value}>{tier.label}</option>
                    ))}
                  </select>
                </div>

                <div className="push-form-group">
                  <label>Store Status</label>
                  <select
                    value={broadcastForm.targetLive}
                    onChange={(e) => setBroadcastForm({ ...broadcastForm, targetLive: e.target.value })}
                  >
                    <option value="all">All Stores</option>
                    <option value="live">Live Stores Only</option>
                    <option value="notlive">Not Live Stores</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="push-form-actions">
              <button type="submit" className="push-send-btn" disabled={sending}>
                {sending ? (
                  <>
                    <RefreshCw size={18} className="spinning" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Broadcast
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {activeTab === 'single' && (
          <form onSubmit={handleSendToMerchant} className="push-form">
            <div className="push-form-section">
              <h3>Select Merchant</h3>

              <div className="push-form-group">
                <label>Search Merchant</label>
                <div className="push-search-wrapper">
                  <Search size={18} />
                  <input
                    type="text"
                    placeholder="Search by email or store name..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      searchMerchants(e.target.value);
                    }}
                  />
                </div>
                {searchResults.length > 0 && (
                  <div className="push-search-results">
                    {searchResults.map((merchant) => (
                      <div
                        key={merchant.id}
                        className="push-search-result"
                        onClick={() => selectMerchant(merchant)}
                      >
                        <div className="merchant-info">
                          <span className="merchant-email">{merchant.email}</span>
                          {merchant.store?.name && (
                            <span className="merchant-store">{merchant.store.name}</span>
                          )}
                        </div>
                        <span className={`merchant-tier ${getTierClass(merchant.merchantTier)}`}>
                          {merchant.merchantTier || 'FREE'}
                        </span>
                        <span className={`merchant-push-state ${merchant.pushEnabled ? 'enabled' : 'disabled'}`}>
                          {merchant.pushEnabled ? 'Push ready' : 'No push'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {singleForm.merchantId && (
                <div className="push-selected-merchant">
                  <Check size={16} color="#10B981" />
                  <span>Selected: {singleForm.merchantEmail}</span>
                </div>
              )}
            </div>

            <div className="push-form-section">
              <h3>Notification Content</h3>

              <div className="push-form-group">
                <label>Title *</label>
                <input
                  type="text"
                  placeholder="e.g., Message from Revstar"
                  value={singleForm.title}
                  onChange={(e) => setSingleForm({ ...singleForm, title: e.target.value })}
                  maxLength={50}
                />
              </div>

              <div className="push-form-group">
                <label>Message *</label>
                <textarea
                  placeholder="Enter your notification message..."
                  value={singleForm.body}
                  onChange={(e) => setSingleForm({ ...singleForm, body: e.target.value })}
                  rows={4}
                  maxLength={200}
                />
              </div>
            </div>

            <div className="push-form-actions">
              <button type="submit" className="push-send-btn" disabled={sending || !singleForm.merchantId}>
                {sending ? (
                  <>
                    <RefreshCw size={18} className="spinning" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Notification
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default PushNotifications;
